/**
 * React hook for metrics tracking
 * Provides easy access to the metrics tracker with the current user's ID
 */

import { useCallback, useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { Authorization } from '../state/authAtoms';
import { initMetrics, track, trackFeature } from '../lib/metrics';

type EventType =
  | 'session_start'
  | 'story_create_opened'
  | 'story_created'
  | 'story_published'
  | 'story_opened'
  | 'story_deleted'
  | 'feature_used'
  | 'referral'
  | 'presentation_started'
  | 'share_clicked'
  | 'qr_generated'
  | 'image_uploaded'
  | 'filter_applied'
  | 'tour_started'
  | 'tour_completed'
  | 'login'
  | 'signup';

type EventData = Record<string, string | number | boolean | undefined>;

/**
 * Hook that provides metrics tracking functions bound to the current user
 */
export const useMetrics = () => {
  const auth = useRecoilValue(Authorization);
  const userId = auth.uid;
  const initializedRef = useRef(false);

  // Initialize metrics when user becomes available
  useEffect(() => {
    if (userId && !initializedRef.current) {
      initializedRef.current = true;
      initMetrics(userId);
    }
  }, [userId]);

  // Reset initialization flag when user logs out
  useEffect(() => {
    if (!userId) {
      initializedRef.current = false;
    }
  }, [userId]);

  const trackEvent = useCallback(
    (eventType: EventType, eventData?: EventData) => {
      track(userId, eventType, eventData);
    },
    [userId]
  );

  const trackFeatureUsage = useCallback(
    (feature: string) => {
      trackFeature(userId, feature);
    },
    [userId]
  );

  return {
    track: trackEvent,
    trackFeature: trackFeatureUsage,
    userId,
    isAuthenticated: auth.isLoggedIn,
  };
};

export default useMetrics;
